import React, { useEffect, useState } from 'react';
import {
  ModalFooter,
  HStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useToast,
  VStack,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Form/Input';
import { CREATE_PAGE, UPDATE_PAGE } from '../../../../graphQL/mutations';
import { GET_PAGES } from '../../../../graphQL/queries';
import Checkbox from '../../../../components/Form/Checkbox';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (page: any) => void;
  onEdited?: (page: any) => void;
  ItemToEdit?: null | any;
}

interface FormData {
  name: string;
  slug?: string;
  vtex_query: string;
  header_type?: string;
  show_tab_bar?: boolean;
}

const createFormData = yup.object().shape({
  name: yup.string().required('El nombre es obligatorio'),
  vtex_query: yup.string().required('La Query Vtex es obligatoria'),
});

const DynamicShowcaseModal: React.FC<Props> = ({
  isOpen,
  onClose,
  ItemToEdit,
}) => {
  const { formState, handleSubmit, register, setValue, reset, control } =
    useForm({
      resolver: yupResolver(createFormData),
    });

  const toast = useToast();
  const { errors } = formState;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [createPage] = useMutation(CREATE_PAGE, {
    refetchQueries: [GET_PAGES, 'GetPages'],
  });

  const [updatePage] = useMutation(UPDATE_PAGE, {
    refetchQueries: [GET_PAGES, 'GetPages'],
  });

  useEffect(() => {
    if (!ItemToEdit) return;
    setValue('name', ItemToEdit.name);
    // setValue('slug', ItemToEdit.slug);
    setValue('vtex_query', ItemToEdit.vtex_query);
    setValue('header_type', ItemToEdit?.header_type);
    setValue('show_tab_bar', ItemToEdit?.show_tab_bar);
  }, [ItemToEdit, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      setLoading(true);
      if (!ItemToEdit) {
        await createPage({
          variables: {
            createPageInput: {
              ...data,
            },
          },
        });

        toast({
          title: t('success'),
          description: t('showcaseCreatedSuccessfully'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        await updatePage({
          variables: {
            updatePageInput: {
              id: ItemToEdit?.id,
              ...data,
            },
          },
        });

        toast({
          title: t('success'),
          description: t('showcaseEditedSuccessfully'),
          status: 'success',
          position: 'top-right',
        });
      }
      reset();
      onClose();
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description: err?.response?.data?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        reset();
      }}
      isOpen={isOpen}
      isCentered
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        maxH="90vh"
        as="form"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <ModalHeader>{t('showCase')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8}>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Input
                isRequired
                label={t('nameField')}
                error={errors.name as FieldError | undefined}
                {...register('name')}
              />
              <Input
                isRequired
                label="Query Vtex"
                error={errors.vtex_query as FieldError | undefined}
                {...register('vtex_query')}
              />
              <Input
                label={t('headerType')}
                error={errors.header_type as FieldError | undefined}
                {...register('header_type')}
              />
            </SimpleGrid>
            <Flex
              width="100%"
              justifyContent="space-around"
              alignItems="center"
            >
              <Checkbox
                control={control}
                label={t('showTabBar')}
                {...register('show_tab_bar')}
              />
            </Flex>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={4}>
            <Button onClick={onClose}>{t('close')}</Button>
            <Button colorScheme="brand" isLoading={loading} type="submit">
              {t('save')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DynamicShowcaseModal;
